/**
 * 챗봇 메시지 1개의 UI.
 *
 * role 별 분기:
 *  - USER         : 우측 정렬 파란 배경
 *  - ASSISTANT    : 좌측 정렬 회색 배경 + Markdown 렌더링
 *  - SYSTEM_RESET : 가운데 구분선 "─── 대화 초기화 (시각) ───"
 *
 * <p>Markdown 은 react-markdown + remark-gfm 으로 안전 렌더링.
 * raw HTML 비활성 (XSS 방지).
 */
import React from "react";
import { Box, Typography } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import dayjs from "dayjs";
import { ds } from "../../styles/designSystem";

function formatTime(iso) {
    if (!iso) return "";
    const d = dayjs(iso);
    return d.isValid() ? d.format("HH:mm") : "";
}

export default function ChatbotMessageBubble({ message, streamingText }) {
    const { role, content, createdAt, isError } = message;
    const displayContent = streamingText != null ? streamingText : content;

    if (role === "SYSTEM_RESET") {
        return (
            <Box sx={{
                display: "flex", alignItems: "center", gap: 1,
                my: 1.5, px: 1,
            }}>
                <Box sx={{ flex: 1, borderTop: `1px dashed ${ds.colors.border}` }} />
                <Typography sx={{ fontSize: 11, color: ds.colors.textMuted, fontWeight: 600 }}>
                    대화 초기화 · {formatTime(createdAt)}
                </Typography>
                <Box sx={{ flex: 1, borderTop: `1px dashed ${ds.colors.border}` }} />
            </Box>
        );
    }

    const isUser = role === "USER";
    return (
        <Box sx={{
            display: "flex",
            justifyContent: isUser ? "flex-end" : "flex-start",
            mb: 1,
        }}>
            <Box sx={{
                maxWidth: "85%",
                minWidth: 0, // flex item 의 기본 min-width:auto 차단 — 자식 pre 가 풍선 밖으로 밀어내는 것 방지
                px: 1.5, py: 1,
                borderRadius: 1.5,
                bgcolor: isUser ? ds.colors.primary : (isError ? ds.colors.errorBg : ds.colors.borderLight),
                color: isUser ? "#fff" : (isError ? "#7f1d1d" : ds.colors.textPrimary),
                fontSize: 14, lineHeight: 1.5,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
                "& p":   { margin: 0, marginBottom: "4px" },
                "& p:last-child": { marginBottom: 0 },
                "& code":{ bgcolor: isUser ? "rgba(255,255,255,0.2)" : ds.colors.border,
                           px: 0.5, borderRadius: 0.5, fontSize: 12.5,
                           wordBreak: "break-all" },
                "& pre": { my: 0.5, maxWidth: "100%", overflowX: "auto", whiteSpace: "pre" },
                "& pre > div": { maxWidth: "100%", overflowX: "auto" },
                "& ul, & ol": { paddingLeft: "20px", margin: "4px 0" },
                "& table": { borderCollapse: "collapse", my: 0.5, fontSize: 12.5,
                             display: "block", maxWidth: "100%", overflowX: "auto" },
                "& th, & td": { border: `1px solid ${ds.colors.border}`, px: 0.6, py: 0.3 },
                "& img": { maxWidth: "100%", height: "auto" },
            }}>
                {isUser ? (
                    displayContent
                ) : (
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            code({ node, inline, className, children, ...props }) {
                                const match = /language-(\w+)/.exec(className || "");
                                return !inline && match ? (
                                    <SyntaxHighlighter
                                        style={oneLight}
                                        language={match[1]}
                                        PreTag="div"
                                        wrapLongLines
                                        customStyle={{
                                            margin: 0,
                                            borderRadius: 6,
                                            fontSize: 12.5,
                                            maxWidth: "100%",
                                            overflowX: "auto",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                        }}
                                        {...props}
                                    >
                                        {String(children).replace(/\n$/, "")}
                                    </SyntaxHighlighter>
                                ) : (
                                    <code className={className} {...props}>
                                        {children}
                                    </code>
                                );
                            },
                        }}
                    >
                        {displayContent || ""}
                    </ReactMarkdown>
                )}
                <Typography sx={{
                    fontSize: 10, opacity: 0.6, mt: 0.4, textAlign: "right",
                }}>
                    {formatTime(createdAt)}
                </Typography>
            </Box>
        </Box>
    );
}
